import { InputHTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';
import IeltsInputResultIcon from '../ResultIcon';
import useIeltsTheme from '../../hooks/useIeltsTheme';
import { IeltsSkill } from '@/__generated__/graphql';

type Props = {
  index?: number;
  result?: boolean;
  className?: string;
  inputClassName?: string;
  id?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

function IeltsInput({ id, index, result, className = '', inputClassName = '', style, ...rest }: Props) {
  const { theme } = useIeltsTheme();

  return (
    <span
      id={id}
      className={twMerge(
        'inline-flex gap-x-[11px] items-center relative w-[150px] max-md:w-[100px] h-[36px] max-md:h-[30px] max-md:max-w-auto text-white',
        className,
      )}
      style={style}
    >
      {typeof index === 'number' && (
        <span
          style={{
            backgroundColor: theme.questionIndexBgColor,
            // The inset lift was pinned to GAMIFICATION's #B3A218 gold while the
            // fill above it already read from the theme.
            boxShadow: `0px 2px 0px 0px ${theme.questionIndexShadowColor} inset`,
          }}
          className="w-[26px] h-[26px] rounded-full flex justify-center items-center select-none shrink-0"
        >
          <span className="text-[#383838] text-[13px] max-md:text-[11px] font-bold">{index}</span>
        </span>
      )}
      {/* The idle border reads `inputBorderColor` as a class rather than an inline
          style so the focus-within rule below can beat it without `!important`.
          The CLASSIC_LIGHT_ORANGE arm used to hardcode #DFCBA5 because the
          theme's own inputBorderColor was #FB9E61, which no call site ever
          wanted; that token now holds #DFCBA5, so one expression covers all
          ten themes. */}
      <span
        className={twMerge(
          'w-full h-full border-[1px] overflow-hidden flex items-center justify-center',
          'bg-ielts-control rounded-[var(--ielts-control-radius)]',
          'border-ielts-input-line focus-within:border-ielts-accent',
        )}
      >
        <input
          {...rest}
          type="text"
          /**
           * `outline-none`, not `outline-0`: the browser's focus ring is drawn
           * with `outline-style: auto`, and a zero width does not suppress that —
           * it stayed on as a blue rect, clipped into a slab by the wrapper's
           * `overflow-hidden`. Focus now shows on the wrapper's border instead,
           * in the theme's own accent.
           */
          className={twMerge(
            'border-none outline-none text-[14px] placeholder:max-md:text-xs font-semibold w-[calc(100%-20px)] h-full',
            'bg-ielts-control text-ielts-fg',
            inputClassName,
          )}
        />
      </span>
      <IeltsInputResultIcon
        className="absolute -top-[2px] max-lg:-top-[5px] -right-2"
        result={result}
      />
    </span>
  );
}

export default IeltsInput;
